import * as React from "react";
import { ITriggerManualJob } from "../../../redux/types/ITriggerManualJob";
import { CREATE_MANUAL_JOB_TITLE, ESI_GENERATION_JOB_TYPE, DISTRIBUTION_KEY, DISTRIBUTION_TITLE_PLURAL } from "../../Consts";
import { getOpenDistributionsThunk, getPoolsThunk, triggerManualJob } from "../../../redux/thunks/HomeThunks";
import { IUsagePoolState } from "../../../redux/types/IUsagePoolState";
import { IDistributionState } from "../../../redux/types/IDistributionState";
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import ActionButton from "../../../repertoire/components/actionButton/ActionButton";
import DropdownDataInput from "../../../repertoire/components/dropdownDataInput/DropdownDataInput";

export interface IESIGenerationParametersProps {
    triggerManualJob: typeof triggerManualJob;
    createNewJob: any;
    setManualJobParameters: (parameters: ITriggerManualJob) => void;
    getOpenDistributions: typeof getOpenDistributionsThunk;
    getPools: typeof getPoolsThunk;
    openDistributions?: IDistributionState[];
    pools?: IUsagePoolState[];
    scheduledJobSet?: (value: boolean) => void;
    onError?: (error: any) => void;
}

interface IESIGenerationParametersState {
    selectedDistribution: string;
    selectedPools: any[];
    generalError?: string;
    isSubmitting: boolean;
}

export class ESIGenerationParameters extends React.Component<
    IESIGenerationParametersProps,
    IESIGenerationParametersState
> {
    state: IESIGenerationParametersState = {
        selectedDistribution: '',
        selectedPools: [],
        generalError: '',
        isSubmitting: false
    };

    componentDidMount() {
        const { getPools, pools, getOpenDistributions, openDistributions } = this.props;
        if (!pools?.length) getPools();
        if (!openDistributions?.length) getOpenDistributions();
    }

    setParams = () => {
        const { setManualJobParameters } = this.props;
        const { selectedDistribution, selectedPools } = this.state;
        setManualJobParameters({
            jobType: ESI_GENERATION_JOB_TYPE,
            jobParameters: JSON.stringify({
                selectedDistribution,
                selectedPools: selectedPools.filter(sp => sp !== 0)
            }),
        });
    };

    getFilteredDistributions = () =>
        (this.props.openDistributions || []).filter(d => d.distributionPools.length > 0);

    getDistributionCodeOptions() {
        let distributionCodes = (this.props.openDistributions || [])
            .filter(d => d.distributionPools.length > 0)
            .map((val) => {
                return {
                    code: `${val.distributionID}`,
                    description: `${val.distributionCode} - ${val.description}`
                };
            });

        return distributionCodes;
    }

    getFilteredPools = (distributionID: string) => {
        const { pools = [] } = this.props;
        const dist = this.getFilteredDistributions().find(d => d.distributionID === Number(distributionID));
        if (!dist) return [];
        const poolIDs = dist.distributionPools.map(dp => dp.poolID);
        return pools.filter(p => poolIDs.includes(p.poolID));
    };

    getPoolsOptions = (filteredPools: IUsagePoolState[]) => {
        const options = filteredPools.map(val => ({
            key: val.poolID,
            text: val.poolCode,
            data: val.poolCode,
        })).sort((a, b) => a.text.localeCompare(b.text));
        return options.length > 1
            ? [{ key: 0, text: "All", data: "All" }, ...options]
            : options;
    };

    handleDistributionChange(value) {
        this.setState({ selectedDistribution: value, selectedPools: [] }, this.setParams);
    }

    handlePoolsChange = (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        this.setState(prevState => {
            const allOptionKey = 0;
            const currentOptions = this.getPoolsOptions(this.getFilteredPools(this.state.selectedDistribution));
            const poolKeys = currentOptions.filter(o => o.key !== allOptionKey).map(o => o.key);

            let selectedPools = [...prevState.selectedPools];

            if (option?.text === "All") {
                selectedPools = option.selected ? [allOptionKey, ...poolKeys] : [];
            } else if (option) {
                selectedPools = selectedPools.includes(option.key)
                    ? selectedPools.filter(k => k !== option.key && k !== allOptionKey)
                    : [...selectedPools, option.key];
            }

            const allSelected = poolKeys.every(key => selectedPools.includes(key));
            selectedPools = allSelected && poolKeys.length > 1
                ? [allOptionKey, ...poolKeys]
                : selectedPools.filter(k => k !== allOptionKey);

            return { selectedPools };
        }, this.setParams);
    };

    renderDropdownTitle = (options?: IDropdownOption[]) => (
        <div>
            <span>{options?.filter(o => o.text !== 'All').map(o => o.text).join(", ")}</span>
        </div>
    );

    onClick = () => {
        const {onError } = this.props;
        try {
            this.setState({ isSubmitting: true });
            if (this.setParamsAndTriggerJob()) {
                this.props.createNewJob();
                this.props.scheduledJobSet?.(true);
            }
        } catch (err: any) {
            onError?.(err?.toString() || "An error occurred");
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    setParamsAndTriggerJob = () => {
        const { setManualJobParameters, triggerManualJob } = this.props;
        const { selectedDistribution, selectedPools } = this.state;
        if (!!selectedDistribution && selectedPools.length > 0) {
            const manualJobParameters: ITriggerManualJob = {
                jobType: ESI_GENERATION_JOB_TYPE,
                jobParameters: JSON.stringify({
                    distributionId: selectedDistribution,
                    poolIds: selectedPools.filter(p => p !== 0),
                })
            };
            setManualJobParameters(manualJobParameters);
            this.setState({ generalError: "" });
            return triggerManualJob(manualJobParameters);
        } else {
            this.setState({ generalError: "Please fill in all the required fields" });
            return false;
        }
    };

    dismissAlert = () => this.setState({ generalError: "" });

    render() {
        const { selectedDistribution: selectedOpenDistributionID, selectedPools, generalError } = this.state;
        const filteredPools = this.getFilteredPools(selectedOpenDistributionID);
        const poolsOptions = this.getPoolsOptions(filteredPools);

        return (
            <>
                {generalError && (
                    <div className="alert alert-danger alert-dismissible">
                        <span>{generalError}</span>
                        <button type="button" className="close" aria-label="Close" onClick={this.dismissAlert}>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                )}
                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                    <DropdownDataInput
                        label={DISTRIBUTION_TITLE_PLURAL}
                        fieldName={DISTRIBUTION_KEY}
                        changeData={this.handleDistributionChange.bind(this)}
                        value={selectedOpenDistributionID}
                        options={this.getDistributionCodeOptions()}
                        allowNull={false}
                        isHidden={false}
                        isMandatory
                    />
                </div>
                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                    <Dropdown
                        label="Pools"
                        selectedKeys={selectedPools}
                        options={poolsOptions}
                        multiSelect
                        onChange={this.handlePoolsChange}
                        required
                        onRenderTitle={this.renderDropdownTitle}
                        disabled={!selectedOpenDistributionID}
                    />
                </div>
                <div className="form-group col-lg-4 col-md-4 col-sm-6 col-xs-12">
                    <ActionButton buttonText={"Cancel"} buttonAction={this.props.createNewJob} className={"button btn-defaultSecondary"}/>
                </div>
                <div className="form-group col-lg-4 col-lg-push-4 col-md-4 col-md-push-4 col-sm-6 col-sm-push-0 col-xs-12 col-xs-push-0">
                    <ActionButton
                        buttonText={CREATE_MANUAL_JOB_TITLE}
                        buttonAction={this.onClick}
                        disabled={this.state.isSubmitting}
                    />
                </div>
            </>
        );
    }
}
